import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Heart, MessageCircle, Share2, Play, Image, FileText, TrendingUp, Users, Sparkles, Crown, ChevronRight, Loader2 } from 'lucide-react';
import { getInitials, formatNumber } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_0c49a145-2431-460e-8df4-1bea13020ea4/artifacts/j4k88xee_file_00000000c9b871f8a2879288a4f57131.png";

const feedTabs = [
  { id: 'foryou', label: 'For You', icon: Sparkles },
  { id: 'trending', label: 'Trending', icon: TrendingUp },
  { id: 'ideas', label: 'Ideas', icon: FileText },
  { id: 'photos', label: 'Photos', icon: Image },
  { id: 'videos', label: 'Videos', icon: Play },
  { id: 'creators', label: 'Creators', icon: Crown },
];

export default function PublicHomePage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [creators, setCreators] = useState([]);
  const [stats, setStats] = useState({ users_count: 0, posts_count: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('foryou');

  useEffect(() => {
    fetchPublicData();
  }, []);

  useEffect(() => {
    fetchFilteredPosts();
  }, [activeTab]);

  const fetchPublicData = async () => {
    try {
      const [postsRes, statsRes] = await Promise.all([
        axios.get(`${API}/public/posts?limit=20`),
        axios.get(`${API}/public/stats`)
      ]);
      setPosts(postsRes.data);
      setStats(statsRes.data);
      
      // Extract unique creators from posts
      const uniqueCreators = [];
      const seenIds = new Set();
      postsRes.data.forEach(post => {
        if (post.user && !seenIds.has(post.user.id)) {
          seenIds.add(post.user.id);
          uniqueCreators.push(post.user);
        }
      });
      setCreators(uniqueCreators.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch public data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredPosts = async () => {
    try {
      let endpoint = `${API}/public/posts?limit=20`;
      
      if (activeTab === 'trending') {
        endpoint += '&sort=popular';
      }
      
      const response = await axios.get(endpoint);
      let filteredPosts = response.data;
      
      // Client-side filtering based on tab
      if (activeTab === 'photos') {
        filteredPosts = filteredPosts.filter(p => p.media_type === 'image' || (p.media && p.media.length > 0));
      } else if (activeTab === 'videos') {
        filteredPosts = filteredPosts.filter(p => p.media_type === 'video');
      } else if (activeTab === 'ideas') {
        filteredPosts = filteredPosts.filter(p => !p.media || p.media.length === 0);
      }
      
      setPosts(filteredPosts);
    } catch (error) {
      console.error('Failed to fetch filtered posts:', error);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    return `${Math.floor(diffInSeconds / 86400)}d`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="FityH" className="w-10 h-10 object-contain" />
            <span className="text-2xl font-bold text-primary">FityH</span>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')}>
              Log In
            </Button>
            <Button onClick={() => navigate('/register')} className="rounded-full">
              Sign Up Free
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-background" />
        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            <span className="text-primary">Connect.</span>{' '}
            <span className="text-foreground">Share.</span>{' '}
            <span className="text-primary">Create.</span>{' '}
            <span className="text-foreground">Earn.</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Join a next-gen social platform where ideas, photos, and videos live together — and creators grow.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button 
              size="lg" 
              onClick={() => navigate('/register')}
              className="rounded-full px-8 text-lg"
            >
              Sign Up Free
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => document.getElementById('feed')?.scrollIntoView({ behavior: 'smooth' })}
              className="rounded-full px-8 text-lg"
            >
              Explore Trending
            </Button>
          </div>
          
          {/* Stats */}
          <div className="flex items-center justify-center gap-8 md:gap-16">
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-primary">{formatNumber(stats.users_count)}</p>
              <p className="text-sm text-muted-foreground">Users</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-primary">{formatNumber(stats.posts_count)}</p>
              <p className="text-sm text-muted-foreground">Posts</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-primary">{creators.length}+</p>
              <p className="text-sm text-muted-foreground">Creators</p>
            </div>
          </div>
        </div>
      </section>

      {/* Creator Spotlight */}
      {creators.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-8 border-b border-border/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Crown className="w-5 h-5 text-primary" />
              Creator Spotlight
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/register')}>
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
            {creators.map((creator) => (
              <div 
                key={creator.id}
                onClick={() => navigate('/register')}
                className="flex-shrink-0 w-32 text-center cursor-pointer group"
              >
                <Avatar className="w-20 h-20 mx-auto mb-2 ring-2 ring-primary/50 group-hover:ring-primary transition-all">
                  <AvatarImage src={creator.avatar} />
                  <AvatarFallback className="bg-primary/10 text-primary text-lg">
                    {getInitials(creator.display_name || creator.username)}
                  </AvatarFallback>
                </Avatar>
                <p className="font-medium truncate">{creator.display_name || creator.username}</p>
                <p className="text-xs text-muted-foreground">@{creator.username}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Feed Section */}
      <section id="feed" className="max-w-6xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold mb-6">Discover Content</h2>
        
        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
          {feedTabs.map(({ id, label, icon: Icon }) => (
            <Button
              key={id}
              variant={activeTab === id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(id)}
              className="rounded-full whitespace-nowrap"
            >
              <Icon className="w-4 h-4 mr-1" />
              {label}
            </Button>
          ))}
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : activeTab === 'creators' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {creators.map((creator) => (
              <div 
                key={creator.id}
                onClick={() => navigate('/register')}
                className="bg-card rounded-2xl p-6 text-center cursor-pointer hover:bg-card/80 transition-colors border border-border/50"
              >
                <Avatar className="w-16 h-16 mx-auto mb-3">
                  <AvatarImage src={creator.avatar} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(creator.display_name || creator.username)}
                  </AvatarFallback>
                </Avatar>
                <p className="font-semibold">{creator.display_name || creator.username}</p>
                <p className="text-sm text-muted-foreground">@{creator.username}</p>
                <Button size="sm" className="mt-3 rounded-full w-full">
                  Follow
                </Button>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No posts found for this filter.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.map((post) => (
              <article 
                key={post.id}
                onClick={() => navigate('/register')}
                className="bg-card rounded-2xl overflow-hidden border border-border/50 cursor-pointer hover:border-primary/50 transition-colors"
              >
                {/* Post Media */}
                {post.media && post.media.length > 0 && (
                  <div className="aspect-video bg-secondary relative overflow-hidden">
                    {post.media_type === 'video' ? (
                      <div className="w-full h-full flex items-center justify-center bg-black">
                        <Play className="w-12 h-12 text-white/80" />
                      </div>
                    ) : (
                      <img 
                        src={post.media[0]} 
                        alt="" 
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                  </div>
                )}
                
                {/* Post Content */}
                <div className="p-4">
                  {/* Author */}
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={post.user?.avatar} />
                      <AvatarFallback className="bg-primary/10 text-primary text-sm">
                        {getInitials(post.user?.display_name || post.user?.username)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{post.user?.display_name || post.user?.username}</p>
                      <p className="text-xs text-muted-foreground">@{post.user?.username} · {formatTimeAgo(post.created_at)}</p>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <p className="text-sm line-clamp-3 mb-3">{post.content}</p>
                  
                  {/* Hashtags */}
                  {post.hashtags && post.hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {post.hashtags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-xs text-primary">#{tag}</span>
                      ))}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span className="flex items-center gap-1 text-sm">
                      <Heart className="w-4 h-4" />
                      {formatNumber(post.likes_count)}
                    </span>
                    <span className="flex items-center gap-1 text-sm">
                      <MessageCircle className="w-4 h-4" />
                      {formatNumber(post.comments_count)}
                    </span>
                    <span className="flex items-center gap-1 text-sm">
                      <Share2 className="w-4 h-4" />
                      {formatNumber(post.shares_count || 0)}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
        
        {/* CTA */}
        <div className="text-center py-12">
          <p className="text-lg text-muted-foreground mb-4">
            Join FityH to see more content and connect with creators
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/register')}
            className="rounded-full px-8"
          >
            Sign Up Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="FityH" className="w-8 h-8 object-contain" />
              <span className="text-lg font-bold text-primary">FityH</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/rules" className="hover:text-primary">Terms</Link>
              <Link to="/rules" className="hover:text-primary">Privacy</Link>
              <Link to="/support" className="hover:text-primary">Help</Link>
            </div>
            
            <p className="text-sm text-muted-foreground">
              © 2026 FityH. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
